import React, { useEffect, useMemo, useState } from 'react'

import { useUnit } from 'effector-react'

import { Page } from '@/components/layout/page/page.tsx'
import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { getFormattedDateRange } from '@/components/ui/export'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { WithRefresher } from '@/components/ui/with-refresher'
import { $creatorId } from '@/models/creators'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { CompositionLineChart } from '@/modules/statistics/onlyfans/ui/CompositionLineChart'
import { getAllChattersColumns } from '@/modules/tracking/chatter-tracking/components/all-chatters-table/columns'
import { TopChatterGraph } from '@/modules/tracking/chatter-tracking/components/top-chatters-graph.tsx'
import { ChatterLegendOptions } from '@/modules/tracking/chatter-tracking/components/top-chatters-legend.tsx'
import { useChatterTrackingData } from '@/modules/tracking/chatter-tracking/query/chatter-tracking-query.ts'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'

import { getChatterTableSheetData } from './components/all-chatters-table/export'
import { useChatterTrackingActivityStates } from './hooks/useChatterTrackingActivityStates'
import { $trackingActivityStates } from './models/chatter-activity'
import {
  generateColorMap,
  mapAllChatterData,
  mapTopChatterRevenue,
  mapTopChattersActivity,
  mapTopChattersActivityLegend,
  mapTopChattersMessageAmount,
  mapTopChattersMessageAmountLegend,
  mapTopChattersResponseTime,
  mapTopChattersResponseTimeLegend,
  mapTopChattersRevenue,
  mapTopChattersRevenueLegend,
} from './utils'

export type TopChatterGraphType =
  | 'Activity'
  | 'Revenue'
  | 'MessageAmount'
  | 'ResponseTime'

export const ChatterTrackingPage = () => {
  const { userSettings } = useAccount()
  const creatorId = useUnit($creatorId)
  const { activityStates } = useChatterTrackingActivityStates()
  const shouldShowCents = useMemo(
    () => userSettings?.showCents,
    [userSettings?.showCents],
  )
  const { date, setDate, isOneDay } = useDateRangeContext()
  const { data, isFetching, refetch } = useChatterTrackingData(date)
  const [chatterActivityType, setChatterActivityType] =
    useState<TopChatterGraphType>('Revenue')

  const graphColorMap = useMemo(() => generateColorMap(data), [data])

  const {
    chatterRevenue,
    topChatters,
    topChattersLegend,
    allChatters,
    chatterActivity,
    topChattersActivityLegend,
    chatterMessageAmount,
    topChattersMessageAmountLegend,
    chatterResponseTime,
    topChattersResponseTimeLegend,
  } = useMemo(
    () => ({
      chatterRevenue: mapTopChatterRevenue(data, graphColorMap, isOneDay),
      topChatters: mapTopChattersRevenue(data, graphColorMap),
      topChattersLegend: mapTopChattersRevenueLegend(
        data,
        graphColorMap,
        shouldShowCents,
      ),
      allChatters: mapAllChatterData(data) ?? [],
      chatterActivity: mapTopChattersActivity(data, graphColorMap),
      topChattersActivityLegend: mapTopChattersActivityLegend(
        data,
        graphColorMap,
      ),
      chatterMessageAmount: mapTopChattersMessageAmount(data, graphColorMap),
      topChattersMessageAmountLegend: mapTopChattersMessageAmountLegend(
        data,
        graphColorMap,
      ),
      chatterResponseTime: mapTopChattersResponseTime(data, graphColorMap),
      topChattersResponseTimeLegend: mapTopChattersResponseTimeLegend(
        data,
        graphColorMap,
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, isOneDay, shouldShowCents],
  )

  useEffect(() => {
    if (allChatters.length) {
      trackFeatureUsage(TrackedFeatures.ChatterTracking, creatorId)
    }
  }, [allChatters.length, creatorId])

  const topChatterGraphData = useMemo(() => {
    switch (chatterActivityType) {
      case 'Activity':
        return chatterActivity
      case 'MessageAmount':
        return chatterMessageAmount
      case 'ResponseTime':
        return chatterResponseTime
      default:
        return topChatters
    }
  }, [
    chatterActivityType,
    chatterActivity,
    chatterMessageAmount,
    chatterResponseTime,
    topChatters,
  ])

  const topChatterGraphLegendData = useMemo(() => {
    switch (chatterActivityType) {
      case 'Activity':
        return topChattersActivityLegend
      case 'MessageAmount':
        return topChattersMessageAmountLegend
      case 'ResponseTime':
        return topChattersResponseTimeLegend
      default:
        return topChattersLegend
    }
  }, [
    chatterActivityType,
    topChattersActivityLegend,
    topChattersMessageAmountLegend,
    topChattersResponseTimeLegend,
    topChattersLegend,
  ])

  return (
    <Page
      title="Chatter Tracking"
      headerOptions={
        <WithRefresher
          onRefresh={refetch}
          isFetching={isFetching}
        >
          <CalendarRangeDatePicker
            date={date}
            setDate={setDate}
            dateNameSuffix={
              data?.timezoneOffset ? `(${data?.timezoneOffset})` : undefined
            }
          />
        </WithRefresher>
      }
    >
      <div className="grid gap-8">
        <div className="grid grid-flow-row grid-cols-1 gap-4 xl:gap-8 2xl:grid-cols-2">
          <TopChatterGraph
            data={topChatterGraphData}
            legendData={
              topChatterGraphLegendData as ChatterLegendOptions['legendData']
            }
            isFetching={isFetching}
            topChatterGraphType={chatterActivityType}
            onChangeGraphType={setChatterActivityType}
            isTooltipCurrency={chatterActivityType === 'Revenue'}
            isTooltipMinutes={chatterActivityType === 'Activity'}
            isTooltipSeconds={chatterActivityType === 'ResponseTime'}
            hideTotal={chatterActivityType === 'ResponseTime'}
            showCents={shouldShowCents}
          />
          <CompositionLineChart
            hideZeros
            title="Revenue"
            data={chatterRevenue}
            isFetching={isFetching}
            showCents={shouldShowCents}
          />
        </div>
        {!isFetching && (
          <DataTable
            title="All Chatters"
            columns={getAllChattersColumns(
              shouldShowCents,
              false,
              activityStates || [],
            )}
            data={allChatters}
            enableExport
            exportOptions={{
              fileName: `All Chatters, ${getFormattedDateRange(date)}`,
              getTableSheetData: () => getChatterTableSheetData(allChatters),
            }}
            searchColumn="name"
            enableSearch
            enablePagination
            enableSorting
          />
        )}
        {isFetching && <Skeleton className="h-52 w-full" />}
      </div>
    </Page>
  )
}
